import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Wallet as A1, CreditCard as A2, TrendingDown as A3, Calculator as A4, ShieldCheck as A5, Users as A6 } from 'lucide-react';

import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { 
  DollarSign, GraduationCap, Award, Calculator, PiggyBank,
  TrendingUp, TrendingDown, AlertTriangle, FileText, ExternalLink,
  ChevronRight, Plus, Target, Check, Scale, Download, Copy,
  Building, Briefcase, Clock, BarChart3, Wallet, Info, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import FederalAidMeter from '../components/FederalAidMeter';
import WorkforcePellCallout from '../components/WorkforcePellCallout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Financial Disclaimer Component
const FinancialDisclaimer = ({ compact = false }) => {
  if (compact) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200 text-xs">
        <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p>
          <strong>Disclaimer:</strong> This tool provides estimates for educational purposes only. 
          Consult a qualified financial advisor before making financial decisions.
        </p>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-blue-500/25 bg-blue-500/[0.07] mb-4"
      data-testid="financial-disclaimer">
      <ShieldAlert className="w-3.5 h-3.5 text-blue-200 shrink-0" />
      <p className="text-xs text-blue-50/85">
        Estimates for planning, not financial, tax or legal advice, check anything binding with an aid office or advisor.
      </p>
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Cost Breakdown Card
const CostBreakdownCard = ({ breakdown }) => {
  if (!breakdown) return null;
  
  const stickerPrice = breakdown.sticker_price_annual || 0;
  const netPrice = breakdown.net_price_estimated || 0;
  const savings = stickerPrice - netPrice;
  const savingsPercent = stickerPrice > 0 ? Math.round((savings / stickerPrice) * 100) : 0;
  
  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">{breakdown.institution_name}</h3>
          <p className="text-slate-300 text-sm">Annual Cost Breakdown</p>
        </div>
        <Badge className="bg-blue-500/20 text-blue-400 border-none">
          {Math.round(breakdown.confidence_score * 100)}% Confidence
        </Badge>
      </div>
      
      {/* Sticker vs Net Price */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <p className="text-red-400 text-sm font-medium">Sticker Price</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(stickerPrice)}</p>
          <p className="text-slate-300 text-xs">Published cost</p>
        </div>
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-emerald-400 text-sm font-medium">Est. Net Price</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(netPrice)}</p>
          <p className="text-slate-300 text-xs">After typical aid ({savingsPercent}% savings)</p>
        </div>
      </div>
      
      {/* Detailed breakdown */}
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-700">
          <span className="text-slate-300">Tuition</span>
          <span className="text-white font-medium">{formatCurrency(breakdown.tuition)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-700">
          <span className="text-slate-300">Mandatory Fees</span>
          <span className="text-white font-medium">{formatCurrency(breakdown.mandatory_fees)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-700">
          <span className="text-slate-300">Room & Board</span>
          <span className="text-white font-medium">{formatCurrency(breakdown.room_board_on_campus)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-700">
          <span className="text-slate-300">Books & Supplies</span>
          <span className="text-white font-medium">{formatCurrency(breakdown.books_supplies)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-700">
          <span className="text-slate-300">Transportation</span>
          <span className="text-white font-medium">{formatCurrency(breakdown.transportation)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-700">
          <span className="text-slate-300">Personal Expenses</span>
          <span className="text-white font-medium">{formatCurrency(breakdown.personal_expenses)}</span>
        </div>
        
        {breakdown.hidden_fees && (
          <div className="flex justify-between items-center py-2 text-amber-400">
            <span className="flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Hidden Fees
            </span>
            <span className="font-medium">{formatCurrency(breakdown.hidden_fees.total)}</span>
          </div>
        )}
      </div>
      
      {/* 4-year totals */}
      <div className="mt-6 p-4 rounded-xl bg-slate-800/50">
        <p className="text-slate-300 text-sm mb-2">4-Year Total</p>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-bold text-white">{formatCurrency(breakdown.total_4_year_net)}</p>
            <p className="text-slate-300 text-xs">Net price estimate</p>
          </div>
          <div className="text-right">
            <p className="text-lg text-slate-300 line-through">{formatCurrency(breakdown.total_4_year_sticker)}</p>
            <p className="text-emerald-400 text-sm">Save {formatCurrency(breakdown.total_4_year_sticker - breakdown.total_4_year_net)}</p>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

// Scholarship Match Card
const ScholarshipCard = ({ match, onTrack }) => {
  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-white font-semibold">{match.name}</h4>
          <p className="text-slate-300 text-sm">{match.provider}</p>
        </div>
        <Badge className={`${
          match.match_score >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
          match.match_score >= 60 ? 'bg-blue-500/20 text-blue-400' :
          'bg-slate-500/20 text-slate-300'
        } border-none`}>
          {match.match_score}% Match
        </Badge>
      </div>
      
      <div className="flex items-center gap-4 mb-3">
        <div>
          <p className="text-2xl font-bold text-white">{formatCurrency(match.amount_typical)}</p>
          <p className="text-slate-300 text-xs">Typical Award</p>
        </div>
        <div className="text-slate-300 text-sm">
          {formatCurrency(match.amount_min)} - {formatCurrency(match.amount_max)}
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2 mb-3">
        {match.is_renewable && (
          <Badge className="bg-purple-500/20 text-purple-400 border-none text-xs">
            Renewable ({match.renewable_years} yrs)
          </Badge>
        )}
        {match.requires_essay && (
          <Badge className="bg-amber-500/20 text-amber-400 border-none text-xs">
            Essay Required
          </Badge>
        )}
        <Badge className="bg-slate-700 text-slate-300 border-none text-xs capitalize">
          {match.scholarship_type?.replace('_', ' ')}
        </Badge>
      </div>
      
      {match.deadline && (
        <p className="text-slate-300 text-sm mb-3">
          <Clock className="w-4 h-4 inline mr-1" />
          Deadline: {new Date(match.deadline).toLocaleDateString()}
        </p>
      )}
      
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onTrack(match)} className="flex-1 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" /> Track This
        </Button>
        {match.application_url && (
          <Button size="sm" variant="outline" className="border-slate-600" asChild>
            <a href={match.application_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};

// Funding Plan Component
const FundingPlanView = ({ plan, onUpdate }) => {
  if (!plan) return null;
  
  const fundedPercent = plan.total_4_year_cost > 0 
    ? Math.round((plan.total_funded / plan.total_4_year_cost) * 100) 
    : 0;
  
  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">Funding Strategy</h3>
          <p className="text-slate-300 text-sm">{plan.institution_name}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-white">{fundedPercent}%</p>
          <p className="text-slate-300 text-sm">Funded</p>
        </div>
      </div>
      
      <Progress value={fundedPercent} className="h-3 mb-6" />
      
      {/* Funding Sources */}
      <div className="space-y-4 mb-6">
        <h4 className="text-white font-medium">Funding Sources</h4>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-emerald-500/10">
            <p className="text-emerald-400 text-xs font-medium">Grants & Scholarships</p>
            <p className="text-white font-bold">
              {formatCurrency(plan.federal_grants + plan.state_grants + plan.institutional_grants + plan.scholarships)}
            </p>
            <p className="text-slate-300 text-xs">Free money</p>
          </div>
          
          <div className="p-3 rounded-lg bg-blue-500/10">
            <p className="text-blue-400 text-xs font-medium">529 Savings</p>
            <p className="text-white font-bold">{formatCurrency(plan.savings_529)}</p>
            <p className="text-slate-300 text-xs">Tax-advantaged</p>
          </div>
          
          <div className="p-3 rounded-lg bg-amber-500/10">
            <p className="text-amber-400 text-xs font-medium">Federal Loans</p>
            <p className="text-white font-bold">{formatCurrency(plan.federal_loans_student)}</p>
            <p className="text-slate-300 text-xs">Student debt</p>
          </div>
          
          <div className="p-3 rounded-lg bg-purple-500/10">
            <p className="text-purple-400 text-xs font-medium">Work Study/Jobs</p>
            <p className="text-white font-bold">{formatCurrency(plan.work_study + plan.part_time_job_income)}</p>
            <p className="text-slate-300 text-xs">Earn while learning</p>
          </div>
        </div>
      </div>
      
      {/* OBBBA True Funding Gap (cap-aware), additive, only shows when caps trigger
          a real_funding_gap different from the headline gap. */}
      {plan.real_funding_gap !== undefined && plan.real_funding_gap > (plan.funding_gap || 0) + 100 && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 mb-6" data-testid="true-funding-gap-panel">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-semibold">Your True Funding Gap (after federal caps)</span>
            </div>
            <span className="text-2xl font-bold text-amber-100">{formatCurrency(plan.real_funding_gap)}</span>
          </div>
          <p className="text-amber-100/80 text-xs mb-3">
            The funding gap above ({formatCurrency(plan.funding_gap || 0)}) doesn&apos;t account for federal borrowing caps
            under OBBBA. Once caps are enforced, your real shortfall is <strong>{formatCurrency(plan.real_funding_gap)}</strong>.
          </p>
          {(plan.cap_warnings || []).length > 0 && (
            <div className="space-y-1 mb-2">
              {plan.cap_warnings.map((w, i) => (
                <div key={i} className="text-xs text-amber-200 flex items-start gap-2" data-testid={`cap-warning-${w.type}`}>
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>{w.type.replace(/_/g, ' ')}:</strong>{' '}
                    requested {formatCurrency(w.requested)} → capped at {formatCurrency(w.capped)}
                    {' '}(over by {formatCurrency(w.over_by)}). {w.message}
                  </span>
                </div>
              ))}
            </div>
          )}
          {plan.grad_plus_available === false && (plan.borrower_type === 'grad' || plan.borrower_type === 'professional') && (
            <div className="mt-3 p-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-xs" data-testid="grad-plus-banner">
              <strong>Grad PLUS Loans no longer available</strong> for new {plan.borrower_type} borrowers after July 1, 2026.
              {plan.grad_plus_transition_note && ` ${plan.grad_plus_transition_note}`}
            </div>
          )}
        </div>
      )}

      {/* Gap Analysis */}
      {plan.funding_gap > 0 && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">Funding Gap</span>
            </div>
            <span className="text-2xl font-bold text-white">{formatCurrency(plan.funding_gap)}</span>
          </div>
          <p className="text-slate-300 text-sm mt-2">
            {plan.gap_percentage}% of total cost still needs funding
          </p>
        </div>
      )}
      
      {/* Federal Aid Eligibility Meter, OBBBA caps vs. gap for this path */}
      <div className="mb-6">
        <FederalAidMeter
          totalCost={plan.total_4_year_cost || 0}
          annualCost={(plan.total_4_year_cost || 0) / 4}
          years={4}
          borrowerType={plan.borrower_type || 'undergrad'}
          dependencyStatus={plan.dependency_status || 'dependent'}
          yearInProgram={1}
          estimatedAnnualAid={((plan.federal_grants || 0) + (plan.state_grants || 0) + (plan.institutional_grants || 0) + (plan.scholarships || 0)) / 4}
        />
      </div>

      {/* Debt Summary */}
      <div className="p-4 rounded-xl bg-slate-800/50">
        <h4 className="text-white font-medium mb-3">Projected Debt</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-slate-300 text-sm">Student Debt</p>
            <p className="text-xl font-bold text-white">{formatCurrency(plan.total_student_debt)}</p>
          </div>
          <div>
            <p className="text-slate-300 text-sm">Monthly Payment</p>
            <p className="text-xl font-bold text-white">{formatCurrency(plan.projected_monthly_payment)}</p>
            <p className="text-slate-300 text-xs">10-year repayment</p>
          </div>
        </div>
      </div>
      
      {/* Recommendations */}
      {plan.recommendations?.length > 0 && (
        <div className="mt-6">
          <h4 className="text-white font-medium mb-2">Recommendations</h4>
          <div className="space-y-2">
            {plan.recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5" />
                {rec}
              </div>
            ))}
          </div>
        </div>
      )}
    </GlassCard>
  );
};

// Loan Calculator Component
const LoanCalculator = () => {
  const { getAuthHeader } = useAuth();
  const [loanData, setLoanData] = useState({
    borrower_type: 'undergrad', // OBBBA: undergrad | grad | professional | parent
    program_start_date: '',
    year_in_program: 1,
    federal_subsidized: 12000,
    federal_unsubsidized: 15000,
    parent_plus: 0,
    grad_plus: 0,
    private_loans: 0,
    expected_salary: 55000,
    repayment_years: 10
  });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/loans/calculate`, loanData, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setResult(response.data);
      if ((response.data?.cap_warnings || []).length > 0) {
        toast.info(`${response.data.cap_warnings.length} federal-cap warning(s) detected, see results.`);
      }
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  // Determine if Grad PLUS field should be hidden per OBBBA
  const isGradOrProf = ['grad', 'professional'].includes(loanData.borrower_type);
  const isPostObbba = !loanData.program_start_date || loanData.program_start_date >= '2026-07-01';
  const hideGradPlus = isGradOrProf && isPostObbba;

  return (
    <GlassCard className="p-6">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Calculator className="w-6 h-6 text-blue-400" />
        Loan Calculator
      </h3>

      {/* Compact disclaimer for calculator */}
      <FinancialDisclaimer compact />

      {/* Borrower type selector, drives OBBBA cap enforcement */}
      <div className="grid md:grid-cols-3 gap-4 mt-6 p-4 rounded-lg bg-slate-800/40 border border-slate-700">
        <div>
          <label className="text-slate-300 text-sm">Borrower Type</label>
          <select
            value={loanData.borrower_type}
            onChange={(e) => setLoanData((prev) => ({ ...prev, borrower_type: e.target.value }))}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white mt-1"
            data-testid="borrower-type-select"
          >
            <option value="undergrad">Undergraduate Student</option>
            <option value="grad">Graduate Student</option>
            <option value="professional">Professional Program</option>
            <option value="parent">Parent (PLUS Borrower)</option>
          </select>
        </div>
        <div>
          <label className="text-slate-300 text-sm">Program Start Date</label>
          <input
            type="date"
            value={loanData.program_start_date}
            onChange={(e) => setLoanData((prev) => ({ ...prev, program_start_date: e.target.value }))}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white mt-1"
            data-testid="program-start-date"
          />
          <p className="text-xs text-slate-300 mt-1">After Jul 1, 2026 = OBBBA rules apply</p>
        </div>
        <div>
          <label className="text-slate-300 text-sm">Year in Program</label>
          <select
            value={loanData.year_in_program}
            onChange={(e) => setLoanData((prev) => ({ ...prev, year_in_program: Number(e.target.value) }))}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white mt-1"
            data-testid="year-in-program"
          >
            <option value={1}>Year 1</option>
            <option value={2}>Year 2</option>
            <option value={3}>Year 3</option>
            <option value={4}>Year 4+</option>
          </select>
        </div>
      </div>

      {hideGradPlus && (
        <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm" data-testid="grad-plus-unavailable-notice">
          <strong>Grad PLUS Loans no longer available</strong> for new {loanData.borrower_type} borrowers starting after July 1, 2026.
          If you already received a Grad PLUS Loan for THIS program before that date, you may qualify under transition rules, confirm with your aid office.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Inputs */}
        <div className="space-y-4">
          <div>
            <label className="text-slate-300 text-sm">Federal Subsidized Loans</label>
            <input
              type="number"
              value={loanData.federal_subsidized}
              onChange={(e) => setLoanData(prev => ({ ...prev, federal_subsidized: Number(e.target.value) }))}
              className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
          
          <div>
            <label className="text-slate-300 text-sm">Federal Unsubsidized Loans</label>
            <input
              type="number"
              value={loanData.federal_unsubsidized}
              onChange={(e) => setLoanData(prev => ({ ...prev, federal_unsubsidized: Number(e.target.value) }))}
              className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
          
          <div>
            <label className="text-slate-300 text-sm">Parent PLUS Loans</label>
            <input
              type="number"
              value={loanData.parent_plus}
              onChange={(e) => setLoanData(prev => ({ ...prev, parent_plus: Number(e.target.value) }))}
              className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
          
          <div>
            <label className="text-slate-300 text-sm">Private Loans</label>
            <input
              type="number"
              value={loanData.private_loans}
              onChange={(e) => setLoanData(prev => ({ ...prev, private_loans: Number(e.target.value) }))}
              className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
          
          <div>
            <label className="text-slate-300 text-sm">Expected Starting Salary</label>
            <input
              type="number"
              value={loanData.expected_salary}
              onChange={(e) => setLoanData(prev => ({ ...prev, expected_salary: Number(e.target.value) }))}
              className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
          
          <Button onClick={handleCalculate} disabled={calculating} className="w-full bg-blue-600 hover:bg-blue-700">
            {calculating ? 'Calculating...' : 'Calculate Repayment'}
          </Button>
        </div>
        
        {/* Results */}
        <div>
          {result && (
            <div className="space-y-4">
              {/* OBBBA Cap Warnings */}
              {(result.cap_warnings || []).length > 0 && (
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="calc-cap-warnings">
                  <h4 className="text-amber-300 font-semibold mb-2 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> Federal Cap Warnings
                  </h4>
                  <div className="space-y-2">
                    {result.cap_warnings.map((w, i) => (
                      <div key={i} className="text-xs text-amber-200" data-testid={`calc-warning-${w.type}`}>
                        <strong>{w.type.replace(/_/g, ' ')}:</strong>{' '}
                        Requested {formatCurrency(w.requested)} → capped at {formatCurrency(w.capped)}.
                        {' '}{w.message}
                      </div>
                    ))}
                  </div>
                  {result.rules_label && (
                    <p className="text-xs text-amber-200/60 mt-2">
                      Rules in effect: {result.rules_label}
                    </p>
                  )}
                </div>
              )}

              {/* Private Loan Warning */}
              {result.private_loan_warning && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="private-loan-warning">
                  <p className="text-red-200 text-xs"><strong>Private loan caution:</strong> {result.private_loan_warning}</p>
                </div>
              )}

              <div className={`p-4 rounded-xl ${
                result.summary.risk_level === 'low' ? 'bg-emerald-500/10 border border-emerald-500/20' :
                result.summary.risk_level === 'medium' ? 'bg-amber-500/10 border border-amber-500/20' :
                'bg-red-500/10 border border-red-500/20'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium capitalize ${
                    result.summary.risk_level === 'low' ? 'text-emerald-400' :
                    result.summary.risk_level === 'medium' ? 'text-amber-400' :
                    'text-red-400'
                  }`}>
                    {result.summary.risk_level} Risk
                  </span>
                  <span className="text-white">
                    Debt-to-Income: {result.summary.debt_to_income_ratio}x
                  </span>
                </div>
                <p className="text-slate-300 text-sm">
                  {result.summary.manageable 
                    ? "Your debt load should be manageable with this salary"
                    : "Consider reducing borrowing or increasing expected salary"
                  }
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-slate-300 text-sm">Total Borrowed (after caps)</p>
                <p className="text-3xl font-bold text-white">{formatCurrency(result.total_borrowed)}</p>
                {result.total_borrowed_requested > result.total_borrowed && (
                  <p className="text-xs text-amber-300 mt-1">
                    Requested {formatCurrency(result.total_borrowed_requested)}, {formatCurrency(result.total_borrowed_requested - result.total_borrowed)} over federal caps
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-slate-300 text-xs">Monthly Payment</p>
                  <p className="text-xl font-bold text-white">{formatCurrency(result.summary.monthly_payment_total)}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-slate-300 text-xs">Total Repaid</p>
                  <p className="text-xl font-bold text-white">{formatCurrency(result.summary.total_repaid)}</p>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-red-500/10">
                <p className="text-red-400 text-xs">Total Interest Paid</p>
                <p className="text-xl font-bold text-white">{formatCurrency(result.summary.total_interest)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
};

// Main Component
export default function AdvancedFinancial() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('costs');
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [scholarships, setScholarships] = useState({ matches: [], potential_total: 0 });
  const [fundingPlan, setFundingPlan] = useState(null);
  const [paths, setPaths] = useState([]);
  const [selectedPathId, setSelectedPathId] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    if (selectedPathId) {
      fetchPathData(selectedPathId);
    }
  }, [selectedPathId]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      // Get paths
      const pathsRes = await axios.get(`${API}/paths/my-paths`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setPaths(pathsRes.data.paths || []);
      
      if (pathsRes.data.paths?.length > 0) {
        setSelectedPathId(pathsRes.data.paths[0].id);
      }
      
      // Get scholarship matches
      const scholarRes = await axios.get(`${API}/scholarships/matches/${user.id}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setScholarships(scholarRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPathData = async (pathId) => {
    try {
      const [costRes, fundingRes] = await Promise.all([
        axios.get(`${API}/costs/breakdown/${pathId}`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/funding/plan/${pathId}`, { ...getAuthHeader(), withCredentials: true })
      ]);
      
      setCostBreakdown(costRes.data);
      setFundingPlan(fundingRes.data);
    } catch (error) {
      console.error('Failed to fetch path data:', error);
    }
  };
  
  const handleTrackScholarship = async (match) => {
    try {
      await axios.post(`${API}/scholarships/track`, {
        scholarship_id: match.id,
        scholarship_name: match.name,
        amount_potential: match.amount_typical,
        status: 'in_progress'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success(`Started tracking ${match.name}`);
    } catch (error) {
      toast.error('Failed to track scholarship');
    }
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }
  
  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="family-funding-arrival"
        eyebrow="Costs & Debt"
        title="What this really costs the family."
        subtitle="The full picture: what the education costs, what aid covers, what gets borrowed, and what the repayment looks like on a real salary, before anybody signs anything."
        accent="#f472b6"
        second="#fbbf24"
        ctaLabel="Run the numbers"
        narration={"This is where the money gets honest. We take the education you are considering, subtract what aid actually covers, and show what is left to borrow. Then we run the repayment on the salary the job really pays, so you can see the monthly cost in the life you designed. Families make the worst decisions here, not because they are careless, but because nobody shows them this page before the paperwork."}
        facts={[
          { icon: A4, head: "Total cost, not sticker", sub: "Tuition, living, fees and the years it takes" },
          { icon: A2, head: "Borrowing, shown", sub: "Interest and the real repayment on a real salary" },
          { icon: A5, head: "Before you sign", sub: "Every number visible while it can still change" }
        ]}
        modules={[
          { icon: A1, label: "Cost of attendance", line: "What a year actually costs at each option", hue: "#f472b6" },
          { icon: A2, label: "Loans and interest", line: "What borrowing costs over ten years, not per month", hue: "#fbbf24" },
          { icon: A3, label: "Repayment reality", line: "Monthly payment against the wage the job pays", hue: "#22d3ee" },
          { icon: A6, label: "Family contribution", line: "What the household can carry without harm", hue: "#34d399" },
          { icon: A5, label: "Safer alternatives", line: "Cheaper routes to the same outcome, where they exist", hue: "#a78bfa" },
          { icon: A4, label: "The verdict", line: "Worth it, tight, or not worth it, and why", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Loan-rule and disclaimer banners were pushing the actual numbers below the fold,             the legal line now lives at the bottom of the page instead. */}
        <WorkforcePellCallout />
        
        {/* Header with Student Context */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white" data-testid="student-financial-title">Student Financial Intelligence</h1>
          <p className="text-slate-300 mt-1">Understand the real cost of your path and how to pay for it</p>
        </div>
        
        {/* How to Use This Page */}
        <GlassCard className="p-5 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20" data-testid="student-fi-explainer">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-400" />
            What is this page?
          </h3>
          <p className="text-slate-300 text-sm mb-3">
            This is <strong className="text-white">your personal money dashboard</strong> for the paths you're exploring. 
            Select a path below to see exactly what it will cost, find scholarships you qualify for, 
            and build a plan to cover the gap.
          </p>
          <div className="grid sm:grid-cols-4 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-slate-800/50">
              <DollarSign className="w-4 h-4 text-red-400 mb-1" />
              <p className="text-white font-medium">Real Costs</p>
              <p className="text-slate-300 text-xs">Tuition, housing, books, the full picture</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50">
              <Award className="w-4 h-4 text-emerald-400 mb-1" />
              <p className="text-white font-medium">Scholarships</p>
              <p className="text-slate-300 text-xs">Matches based on your profile</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50">
              <Target className="w-4 h-4 text-purple-400 mb-1" />
              <p className="text-white font-medium">Funding Plan</p>
              <p className="text-slate-300 text-xs">Savings + aid + loans = covered</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50">
              <Calculator className="w-4 h-4 text-amber-400 mb-1" />
              <p className="text-white font-medium">Loan Calculator</p>
              <p className="text-slate-300 text-xs">See what you'd owe monthly after graduation</p>
            </div>
          </div>
          <p className="text-slate-300 text-xs mt-3">
            Tip: Your parent/guardian has their own Financial Intelligence page where they plan savings and funding across your family.
          </p>
        </GlassCard>
        
        {/* Path Selector */}
        {paths.length > 0 && (
          <div className="flex gap-2">
            {paths.map((path) => (
              <Button
                key={path.id}
                onClick={() => setSelectedPathId(path.id)}
                variant={selectedPathId === path.id ? "default" : "outline"}
                className={selectedPathId === path.id ? "bg-blue-600" : "border-slate-600"}
                data-testid={`path-select-${path.id}`}
              >
                {path.institution_name}
              </Button>
            ))}
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {costBreakdown ? formatCurrency(costBreakdown.total_4_year_net) : '$0'}
                </p>
                <p className="text-slate-300 text-sm">4-Year Cost</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{scholarships.total_matches}</p>
                <p className="text-slate-300 text-sm">Scholarships Found</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(scholarships.potential_total)}
                </p>
                <p className="text-slate-300 text-sm">Potential Aid</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {fundingPlan ? formatCurrency(fundingPlan.funding_gap) : '$0'}
                </p>
                <p className="text-slate-300 text-sm">Funding Gap</p>
              </div>
            </div>
          </GlassCard>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-slate-800/50 p-1 mb-6 grid grid-cols-3">
            <TabsTrigger value="costs" className="data-[state=active]:bg-blue-600">
              <DollarSign className="w-4 h-4 mr-2" /> Real Costs
            </TabsTrigger>
            <TabsTrigger value="funding" className="data-[state=active]:bg-blue-600">
              <Wallet className="w-4 h-4 mr-2" /> Funding Plan
            </TabsTrigger>
            <TabsTrigger value="calculator" className="data-[state=active]:bg-blue-600">
              <Calculator className="w-4 h-4 mr-2" /> Loan Calculator
            </TabsTrigger>
          </TabsList>
          
          {/* Costs Tab */}
          <TabsContent value="costs">
            {costBreakdown ? (
              <CostBreakdownCard breakdown={costBreakdown} />
            ) : (
              <GlassCard className="p-12 text-center">
                <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Cost Data</h3>
                <p className="text-slate-300">Select a path to see detailed cost breakdown</p>
              </GlassCard>
            )}
          </TabsContent>
          
          {/* Funding Tab */}
          <TabsContent value="funding">
            <FundingPlanView plan={fundingPlan} />
          </TabsContent>
          
          {/* Calculator Tab */}
          <TabsContent value="calculator">
            <LoanCalculator />
          </TabsContent>
        </Tabs>

        {/* The legal line, once, at the end */}
        <FinancialDisclaimer compact />
      </div>
    </AppLayout>
  );
}
